import { createElement } from 'react';
import { toast } from '@/lib/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';

/**
 * Lote D (G2): el servidor corta a 120 guardados por minuto por oficina y responde 429. Un solo lugar (el interceptor de
 * axios) muestra el aviso, en vez de una pantalla por pantalla. «Reintentar» repite la MISMA operación UNA vez cada
 * vez que la persona lo toca: nunca reintenta solo. Quien hizo la llamada original queda esperando: recibe el resultado del
 * reintento si lo toca, o el error original si el aviso vence sin tocarlo.
 */
export const MENSAJE_LIMITE_DE_GUARDADOS =
  'Se hicieron muchos guardados seguidos desde tu oficina. Esperá alrededor de un minuto y reintentá.';

/**
 * D9 (16-oct): el cupo se renueva cada MINUTO, no cada 10 segundos. Si el servidor manda `Retry-After` (segundos) se
 * usa ese tiempo; si no, o si no es un número, «alrededor de un minuto».
 */
export function mensajeDelLimite(retryAfter?: unknown): string {
  const seg = typeof retryAfter === 'string' ? Number(retryAfter.trim()) : Number(retryAfter);
  if (!retryAfter || !Number.isFinite(seg) || seg <= 0) return MENSAJE_LIMITE_DE_GUARDADOS;
  // De 50 a 89 segundos sigue siendo «alrededor de un minuto»; de 90 en adelante se dicen los minutos (M2, 16-oct).
  if (seg >= 90) {
    const minutos = Math.round(seg / 60);
    return `Se hicieron muchos guardados seguidos desde tu oficina. Esperá unos ${minutos} minutos y reintentá.`;
  }
  if (seg >= 50) return MENSAJE_LIMITE_DE_GUARDADOS;
  const espera = Math.ceil(seg);
  return `Se hicieron muchos guardados seguidos desde tu oficina. Esperá unos ${espera} segundo${espera === 1 ? '' : 's'} y reintentá.`;
}

/** Mientras el aviso está en pantalla, otros 429 seguidos no apilan más avisos. */
let avisoVisible = false;
const DURACION_MS = 15_000;

/** Devuelve false si ya había un aviso en pantalla (no se muestra otro y nadie queda esperando). */
export function mostrarAvisoDeLimite(
  reintentar: () => void,
  alVencer: () => void,
  /** El `Retry-After` de la respuesta 429, en segundos, si vino. */
  retryAfter?: unknown
): boolean {
  if (avisoVisible) return false;
  avisoVisible = true;
  let reintentado = false;
  const t = toast({
    variant: 'destructive',
    title: 'Demasiados guardados seguidos',
    description: mensajeDelLimite(retryAfter),
    duration: DURACION_MS,
    action: createElement(
      ToastAction,
      {
        altText: 'Reintentar',
        onClick: () => {
          reintentado = true;
          t.dismiss();
          avisoVisible = false;
          reintentar();
        },
      },
      'Reintentar'
    ) as never,
  });
  setTimeout(() => {
    avisoVisible = false;
    if (!reintentado) alVencer();
  }, DURACION_MS);
  return true;
}

/** Pruebas: vuelve al estado inicial. */
export function olvidarAvisoDeLimite(): void {
  avisoVisible = false;
}
